import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, FileText, Paperclip, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button, DataTable, FilterBar, FormField, Modal, PageHeader, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { getApiErrorMessage } from '@/lib/axios';
import { usePerms } from '@/hooks/usePerms';
import { formatDateDZ } from '@/utils/algeria';
import { uploadFile } from '@/services/uploads';
import {
  type DocumentEntrepriseDTO, type DocumentEntrepriseInput, type StatutExpiration, type TypeDocumentEntreprise,
  TYPE_DOC_LABEL, TYPE_DOC_ORDRE, createDocument, deleteDocument, getAlertesDocuments, listDocuments, updateDocument,
} from '@/services/documentsEntreprise';
import type { StatusKey } from '@/types';
import { toast } from '@/store/toastStore';

const EXP_BADGE: Record<StatutExpiration, { status: StatusKey; label: (j: number | null) => string } | null> = {
  EXPIRE: { status: 'stuck', label: (j) => `Expiré${j != null ? ` (${-j} j)` : ''}` },
  EXPIRE_BIENTOT: { status: 'working', label: (j) => `Expire dans ${j} j` },
  A_JOUR: { status: 'done', label: () => 'À jour' },
  SANS: null,
};

interface FormValues {
  type: TypeDocumentEntreprise; libelle: string; date_emission: string; date_expiration: string;
  exercice: string; is_active: boolean; notes: string;
}

export function ReferentielDocumentairePage() {
  const perms = usePerms('soumissions');
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentEntrepriseDTO | null>(null);
  const [fichier, setFichier] = useState<{ url: string; filename: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['documents-entreprise', { search, showArchived }],
    queryFn: () => listDocuments({ q: search || undefined, archives: showArchived }),
  });
  const { data: alertes } = useQuery({ queryKey: ['documents-alertes'], queryFn: getAlertesDocuments });

  const { register, handleSubmit, reset } = useForm<FormValues>();
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['documents-entreprise'] }); qc.invalidateQueries({ queryKey: ['documents-alertes'] }); };

  const toPayload = (v: FormValues): DocumentEntrepriseInput => ({
    type: v.type, libelle: v.libelle,
    date_emission: v.date_emission || null, date_expiration: v.date_expiration || null,
    exercice: v.exercice ? Number(v.exercice) : null, is_active: !!v.is_active, notes: v.notes || null,
    fichier_url: fichier?.url ?? null,
  });
  const save = useMutation({
    mutationFn: (v: FormValues) => editing ? updateDocument(editing.id, toPayload(v)) : createDocument(toPayload(v)),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? 'Document mis à jour.' : 'Document ajouté.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const del = useMutation({ mutationFn: deleteDocument, onSuccess: () => { invalidate(); toast.success('Document supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const archive = useMutation({ mutationFn: ({ id, v }: { id: string; v: boolean }) => updateDocument(id, { is_archived: v }), onSuccess: (_d, x) => { invalidate(); toast.success(x.v ? 'Archivé.' : 'Restauré.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  const openCreate = () => { setEditing(null); setFichier(null); reset({ type: 'RC', libelle: '', date_emission: '', date_expiration: '', exercice: '', is_active: true, notes: '' }); setOpen(true); };
  const openEdit = (d: DocumentEntrepriseDTO) => {
    setEditing(d); setFichier(d.fichier_url ? { url: d.fichier_url, filename: d.fichier_url.split('/').pop() || 'document.pdf' } : null);
    reset({ type: d.type, libelle: d.libelle, date_emission: d.date_emission ?? '', date_expiration: d.date_expiration ?? '', exercice: d.exercice?.toString() ?? '', is_active: d.is_active, notes: d.notes ?? '' });
    setOpen(true);
  };
  const onFile = async (f: File | undefined) => { if (!f) return; setUploading(true); try { setFichier(await uploadFile(f)); } catch (e) { toast.error(getApiErrorMessage(e)); } finally { setUploading(false); } };

  const columns: ColumnDef<DocumentEntrepriseDTO, unknown>[] = [
    { accessorKey: 'type', header: 'Type', size: 180, cell: ({ getValue }) => <span className="text-xs font-semibold text-brand-primary">{TYPE_DOC_LABEL[getValue() as TypeDocumentEntreprise]}</span> },
    { accessorKey: 'libelle', header: 'Libellé', cell: ({ row }) => <span className="font-medium text-content-primary">{row.original.libelle}{row.original.exercice ? <span className="ml-1 text-xs text-content-muted">· {row.original.exercice}</span> : null}</span> },
    { accessorKey: 'date_emission', header: 'Émission', size: 110, cell: ({ getValue }) => (getValue() ? formatDateDZ(getValue() as string) : '—') },
    {
      accessorKey: 'date_expiration', header: 'Expiration', size: 150,
      cell: ({ row }) => {
        const b = EXP_BADGE[row.original.statut_expiration];
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-sm text-content-secondary">{row.original.date_expiration ? formatDateDZ(row.original.date_expiration) : '—'}</span>
            {b && <StatusBadge status={b.status} label={b.label(row.original.jours_restants)} />}
          </div>
        );
      },
    },
    { id: 'fichier', header: 'Pièce', size: 70, cell: ({ row }) => row.original.fichier_url ? <a href={row.original.fichier_url} target="_blank" rel="noopener noreferrer" className="inline-flex rounded p-1.5 text-brand-primary hover:bg-brand-primary/10" title="Ouvrir le PDF"><FileText className="size-4" /></a> : <span className="text-xs text-content-muted">—</span> },
    {
      id: 'actions', header: '', size: 120,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {showArchived ? (
            perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => archive.mutate({ id: row.original.id, v: false })} title="Restaurer"><Plus className="size-4" /></Button>
          ) : (
            <>
              {perms.canModifier && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)} title="Modifier"><Pencil className="size-4" /></Button>}
              {perms.canArchiver && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => archive.mutate({ id: row.original.id, v: true })} title="Archiver"><X className="size-4" /></Button>}
            </>
          )}
          {perms.canSupprimer && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm(`Supprimer « ${row.original.libelle} » ?`)) del.mutate(row.original.id); }} title="Supprimer"><Trash2 className="size-4" /></Button>}
        </div>
      ),
    },
  ];

  const nbAlertes = (alertes?.expires.length ?? 0) + (alertes?.expirent_bientot.length ?? 0);

  return (
    <>
      <PageHeader
        title="Référentiel documentaire"
        subtitle="Pièces réutilisables de l'entreprise pour les dossiers de soumission (RC, NIF, attestations, bilans…)"
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: 'Référentiel documentaire' }]}
        actions={perms.canAjouter ? <Button onClick={openCreate}><Plus /> Nouveau document</Button> : undefined}
      />

      {nbAlertes > 0 && (
        <div className="mb-4 rounded-lg border border-status-stuck/30 bg-status-stuck/5 p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-status-stuck"><AlertTriangle className="size-4" /> {nbAlertes} pièce(s) à renouveler — un dossier avec une pièce périmée est irrecevable</div>
          <ul className="space-y-1 text-sm">
            {alertes?.expires.map((d) => <li key={d.id} className="text-status-stuck">• <strong>{d.libelle}</strong> — expiré le {formatDateDZ(d.date_expiration!)}</li>)}
            {alertes?.expirent_bientot.map((d) => <li key={d.id} className="text-status-working">• {d.libelle} — expire dans {d.jours_restants} j ({formatDateDZ(d.date_expiration!)})</li>)}
          </ul>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Rechercher un document…" />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-content-secondary">
          <input type="checkbox" className="size-4 accent-brand-primary" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Afficher les archivés
        </label>
      </div>

      <DataTable columns={columns} data={data?.items ?? []} loading={isLoading} pageSize={15} exportFileName="documents-entreprise" emptyMessage={showArchived ? 'Aucun document archivé.' : 'Aucun document. Ajoutez les pièces de l\'entreprise (RC, NIF, attestations…).'} />

      <Modal open={open} onOpenChange={setOpen} title={editing ? 'Modifier le document' : 'Nouveau document'} size="lg"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={handleSubmit((v) => save.mutate(v))} loading={save.isPending}>{editing ? 'Enregistrer' : 'Ajouter'}</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Type de document" required>
            <select {...register('type')} className="flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm">
              {TYPE_DOC_ORDRE.map((t) => <option key={t} value={t}>{TYPE_DOC_LABEL[t]}</option>)}
            </select>
          </FormField>
          <FormField label="Exercice (année)" hint="Pour les bilans notamment"><Input type="number" step="1" placeholder="2025" {...register('exercice')} /></FormField>
          <FormField label="Libellé" required className="sm:col-span-2"><Input placeholder="Ex. Attestation CNAS 2026" {...register('libelle')} /></FormField>
          <FormField label="Date d'émission"><Input type="date" {...register('date_emission')} /></FormField>
          <FormField label="Date d'expiration" hint="Laisser vide si sans échéance"><Input type="date" {...register('date_expiration')} /></FormField>
          <FormField label="Pièce jointe (PDF)" className="sm:col-span-2">
            {fichier ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-surface-secondary px-3 py-2 text-sm">
                <span className="flex items-center gap-2 truncate text-content-primary"><Paperclip className="size-4 shrink-0" /><span className="truncate">{fichier.filename}</span></span>
                <span className="flex items-center gap-2">
                  <a href={fichier.url} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline" title="Voir"><FileText className="size-4" /></a>
                  <button type="button" onClick={() => setFichier(null)} className="text-content-muted hover:text-status-stuck" title="Retirer"><X className="size-4" /></button>
                </span>
              </div>
            ) : (
              <Input type="file" accept="application/pdf" disabled={uploading} onChange={(e) => onFile(e.target.files?.[0])} />
            )}
          </FormField>
          <FormField label="Notes" className="sm:col-span-2"><Input {...register('notes')} /></FormField>
          <label className="flex items-center gap-2 text-sm text-content-secondary sm:col-span-2">
            <input type="checkbox" className="size-4 accent-brand-primary" {...register('is_active')} /> Document actif (utilisable dans les dossiers)
          </label>
        </div>
      </Modal>
    </>
  );
}
